// ABOUTME: Builds a row-major texture atlas of country flag PNGs for pennants.
// ABOUTME: One CanvasTexture, one draw call — pennants index cells via aFlagIndex.

import * as THREE from 'three';

export const ATLAS_GRID_COLS = 8;
export const ATLAS_GRID_ROWS = 6;
export const ATLAS_CELL_W = 256;
export const ATLAS_CELL_H = 170;
export const ATLAS_CELL_PADDING_PX = 2;

// Vite serves this app under a configurable base path (see vite.config.js).
// Resolve flag PNGs against that base so the URLs work whether the app is
// mounted at `/` or at `/static/visuals/2026/hopp/`.
const BASE_URL = import.meta.env?.BASE_URL ?? './';
const FLAG_URL_PREFIX = `${BASE_URL}flags/`;

/**
 * Row-major cell origin in normalised atlas-canvas coords.
 * Returns the (u, v) of the top-left of the cell at `index` for a grid sized
 * `gridCols × gridRows`. Pure — no DOM, no THREE — and the natural input to
 * the shader's atlas-sampling math (after any flipY adjustment).
 *
 * @returns {{u: number, v: number}}
 */
export function flagAtlasCellOrigin(index, gridCols, gridRows) {
    if (!Number.isInteger(index) || index < 0) {
        throw new Error(`flagAtlasCellOrigin: index must be a non-negative integer, got ${index}`);
    }
    if (!Number.isInteger(gridCols) || gridCols <= 0 ||
        !Number.isInteger(gridRows) || gridRows <= 0) {
        throw new Error('flagAtlasCellOrigin: grid dimensions must be positive integers');
    }
    if (index >= gridCols * gridRows) {
        throw new Error(`flagAtlasCellOrigin: index ${index} out of range for ${gridCols}×${gridRows}`);
    }
    const col = index % gridCols;
    const row = Math.floor(index / gridCols);
    return { u: col / gridCols, v: row / gridRows };
}

/**
 * Build a Map<code, index> in `teams[]` order. Pure helper used both by the
 * runtime atlas builder and by tests that don't need a canvas.
 */
export function flagAtlasIndexByCode(teams) {
    const map = new Map();
    teams.forEach((team, i) => map.set(team.code, i));
    return map;
}

/**
 * Build a row-major flag atlas from a list of teams.
 *
 * Layout: 8 cols × 6 rows of 256×170 cells with 2 px transparent padding on
 * every side. The PNG (or solid-colour fallback on load failure) is painted
 * inside the padded inner rect so cell-edge bleed is impossible.
 *
 * @param {Array<{code:string, primary:string}>} teams — order defines indices.
 * @param {{anisotropy?: number}} [options]
 * @returns {Promise<{texture: THREE.CanvasTexture, indexByCode: Map<string,number>, gridCols: number, gridRows: number, cellPaddingPx: number}>}
 */
export async function buildFlagAtlas(teams, { anisotropy = 4 } = {}) {
    if (!Array.isArray(teams) || teams.length === 0) {
        throw new Error('buildFlagAtlas: teams must be a non-empty array');
    }
    const maxCells = ATLAS_GRID_COLS * ATLAS_GRID_ROWS;
    if (teams.length > maxCells) {
        throw new Error(
            `buildFlagAtlas: ${teams.length} teams exceeds atlas capacity (${maxCells})`
        );
    }

    const canvas = document.createElement('canvas');
    canvas.width = ATLAS_GRID_COLS * ATLAS_CELL_W;
    canvas.height = ATLAS_GRID_ROWS * ATLAS_CELL_H;
    const ctx = canvas.getContext('2d');
    // Cells start fully transparent; failed loads paint a solid primary panel.
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const indexByCode = flagAtlasIndexByCode(teams);

    await Promise.all(teams.map((team, i) => paintCell(ctx, team, i)));

    const texture = new THREE.CanvasTexture(canvas);
    texture.colorSpace = THREE.SRGBColorSpace;
    texture.minFilter = THREE.LinearFilter;
    texture.magFilter = THREE.LinearFilter;
    texture.generateMipmaps = false;
    texture.anisotropy = Math.max(1, anisotropy);
    texture.needsUpdate = true;

    return {
        texture,
        indexByCode,
        gridCols: ATLAS_GRID_COLS,
        gridRows: ATLAS_GRID_ROWS,
        cellPaddingPx: ATLAS_CELL_PADDING_PX,
    };
}

function paintCell(ctx, team, index) {
    const { u, v } = flagAtlasCellOrigin(index, ATLAS_GRID_COLS, ATLAS_GRID_ROWS);
    const x = u * ATLAS_GRID_COLS * ATLAS_CELL_W + ATLAS_CELL_PADDING_PX;
    const y = v * ATLAS_GRID_ROWS * ATLAS_CELL_H + ATLAS_CELL_PADDING_PX;
    const innerW = ATLAS_CELL_W - 2 * ATLAS_CELL_PADDING_PX;
    const innerH = ATLAS_CELL_H - 2 * ATLAS_CELL_PADDING_PX;

    return loadFlagImage(team).then(
        (img) => { ctx.drawImage(img, x, y, innerW, innerH); },
        () => {
            // PNG missing or failed to decode — fall back to the team's primary
            // colour so the pennant still reads as that team rather than empty.
            ctx.fillStyle = team.primary || '#888888';
            ctx.fillRect(x, y, innerW, innerH);
        }
    );
}

function flagUrl(team) {
    // teams.json's emblem field is an absolute path (e.g. "/flags/sui.png")
    // that ignores Vite's base; rebuild the URL against BASE_URL so the
    // request lands at the correctly-mounted asset path.
    const filename = team.emblem
        ? team.emblem.split('/').pop()
        : `${team.code.toLowerCase()}.png`;
    return `${FLAG_URL_PREFIX}${filename}`;
}

function loadFlagImage(team) {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error(`flag PNG failed: ${team.code}`));
        img.src = flagUrl(team);
    });
}
